import React, { MouseEvent, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import FocusLock from 'react-focus-lock'
import Sites from '@toasttab/shared/src/data/Sites.json'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as CloseIcon } from '@toasttab/shared/src/assets/svg/icon-close.svg'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as IconWorkplaceEarth } from '@toasttab/shared/src/assets/svg/icon-workplace-earth.svg'
import { SiteSchema } from '@toasttab/shared/src/interfaces/Site.d'
import { formatUrl, getRegionCode } from '@toasttab/shared/src/utils/siteUtils'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import { isLocaleHidden } from '@toasttab/shared/src/config/localeConfig'

export interface RegionLanguageSelectorProps {
  currentLanguage?: string
  footerLinkCss: string
  localizedEntries?: any[]
  packageName: string
  siteId: number
  hostName?: string
}

const RegionLanguageSelector = ({
  footerLinkCss,
  localizedEntries = [],
  packageName,
  siteId
}: RegionLanguageSelectorProps) => {
  const { t } = useI18next()
  const { isDataPage } = usePageContext()
  const [showModal, setShowModal] = useState(false)

  const activeSite = useMemo(
    () => Sites.sites.find((site) => site.siteId === siteId),
    [siteId]
  )
  const pkg = useMemo(
    () => Sites.packages.find((p) => p.package === packageName),
    [packageName]
  )

  const getPackagePath = () => {
    if (isDataPage) return '/blog/data'
    return pkg?.path || ''
  }

  useEffect(() => {
    const handleEsc = (event: any) => {
      if (
        event.key === 'Escape' ||
        event.key === 'ESC' ||
        event.keyCode === 27
      ) {
        setShowModal(false)
      }
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [setShowModal])

  const handleRegionSelection = (site: SiteSchema & { url: string }) => {
    // Set cookies to remember user's preference
    _helpers.createCookie(
      '_preferredRegion',
      getRegionCode(site.contentfulRegionCode),
      30,
      '/'
    )
    _helpers.createCookie('_userLanguage', site.language, 365, '/')

    // Navigate to the selected region
    window.location.href = site.url || '/'
  }

  const modalClasses = classNames(
    'fixed bg-black bg-opacity-30 inset-0 w-full z-50 flex items-center justify-center p-4',
    { hidden: !showModal }
  )

  const getCurrentSiteName = () =>
    Sites.sites
      .filter((site) => !isLocaleHidden(site.language))
      .find((site) => site.siteId === siteId)?.siteName || 'US'

  const groupedSites = useMemo(() => {
    return Sites.sites.reduce<Record<string, SiteSchema[]>>((acc, site) => {
      const entry = localizedEntries.find((e) => e.language === site.language)

      // Only include sites that have corresponding entries in localizedEntries
      // This respects the locale filtering from getAllEntries()
      if (!entry) return acc

      const fallback = localizedEntries.find(
        (e) => e.language === activeSite?.language
      )
      const slug =
        entry?.slugOverride ||
        entry?.slug ||
        fallback?.slugOverride ||
        fallback?.slug ||
        ''
      const url = formatUrl(site, getPackagePath(), slug)
      acc[site.continent] = acc[site.continent] || []
      acc[site.continent].push({ ...site, url })
      return acc
    }, {})
  }, [localizedEntries, isDataPage, pkg?.path, activeSite?.language])

  return (
    <>
      <button
        className={`${footerLinkCss} before:content-icon-map-marker before:mr-2`}
        data-testid='RegionLanguageSelector-openButton'
        onClick={() => setShowModal(true)}
      >
        {t('Region')}: {t(getCurrentSiteName())}
      </button>

      <div
        aria-hidden={!showModal}
        className={modalClasses}
        data-testid='RegionLanguageSelector-modal'
        onClick={() => setShowModal(false)}
        role='dialog'
      >
        <FocusLock disabled={!showModal}>
          <div
            className='relative w-full max-w-xl max-h-[800px] bg-white rounded-lg shadow-xl overflow-y-auto'
            onClick={(e: MouseEvent) => e.stopPropagation()}
          >
            <button
              aria-label='Close region selector'
              className='absolute top-1 right-1'
              data-testid='RegionLanguageSelector-closeButton'
              onClick={() => setShowModal(false)}
            >
              <CloseIcon height='30' width='30' />
            </button>

            <nav aria-label='Switch language' className='p-10'>
              <IconWorkplaceEarth height='28' width='28' />
              <h4 className='text-2xl font-normal mb-6'>
                {t('Choose your country and language')}:
              </h4>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {Object.entries(groupedSites).map(([continent, sites]) => (
                  <div key={continent}>
                    <h3 className='uppercase text-sm mb-2 font-sansPro font-semibold'>
                      {t(continent)}
                    </h3>
                    <ul>
                      {sites
                        .filter((site) => !isLocaleHidden(site.language))
                        .map((site) => (
                          <li className='mb-2' key={`site-${site.siteId}`}>
                            <button
                              className='font-normal text-blue-75 text-base text-left hover:underline'
                              onClick={() => handleRegionSelection(site)}
                            >
                              {t(site.languageName)}
                            </button>
                          </li>
                        ))}
                    </ul>
                  </div>
                ))}
              </div>
            </nav>
          </div>
        </FocusLock>
      </div>
    </>
  )
}

export default RegionLanguageSelector
